import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a comma-separated string into an array of trimmed strings
 * @param input - Comma-separated string (e.g., "apple, banana, orange")
 * @returns Array of trimmed strings (e.g., ["apple", "banana", "orange"])
 */
export function parseCommaSeparatedString(
  input: string | undefined | null
): string[] {
  if (!input || typeof input !== "string") {
    return [];
  }
  return input
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Converts an array of strings into a comma-separated string
 * @param array - Array of strings (e.g., ["apple", "banana", "orange"])
 * @returns Comma-separated string (e.g., "apple, banana, orange")
 */
export function arrayToCommaSeparatedString(array: string[]): string {
  return array.join(", ");
}

/**
 * Formats a signal type enum value for display
 * @param type - Signal type enum value (e.g., "TYPE_OF_OPERATOR")
 * @returns Formatted string (e.g., "Type of Operator")
 */
export function formatSignalType(type: string): string {
  return type
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Formats a price signal type for display
 * @param signalType - Signal type enum value (e.g., "POSITIVE")
 * @returns Formatted string (e.g., "Positive")
 */
export function formatPriceSignalType(signalType: string): string {
  return signalType === "POSITIVE" ? "Positive" : "Negative";
}

/**
 * Formats a comparison type for display
 * @param comparisonType - Comparison type enum value (e.g., "OVER")
 * @returns Formatted string (e.g., "Over")
 */
export function formatComparisonType(comparisonType: string): string {
  return comparisonType === "OVER" ? "Over" : "Under";
}

export function normalizeWebsiteUrl(url: string): string | null {
  const WebsiteSchema = z
    .string()
    .url()
    .refine(
      (url) => {
        try {
          const urlObj = new URL(url);
          return urlObj.protocol === "http:" || urlObj.protocol === "https:";
        } catch {
          return false;
        }
      },
      { message: "Invalid URL format" }
    );

  if (!url || !url.trim()) return null;

  const trimmedUrl = url.trim();

  try {
    // Try to create URL object directly first
    let urlObj: URL;

    if (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) {
      urlObj = new URL(trimmedUrl);
    } else {
      // Add https:// if no protocol
      urlObj = new URL(`https://${trimmedUrl}`);
    }

    // Convert http to https
    if (urlObj.protocol === "http:") {
      urlObj.protocol = "https:";
    }

    const normalizedUrl = urlObj.toString();

    // Validate with Zod schema
    const result = WebsiteSchema.safeParse(normalizedUrl);
    return result.success ? normalizedUrl : null;
  } catch {
    // URL constructor will throw for invalid URLs
    return null;
  }
}

/**
 * Formats a date for display
 * @param date - Date to format
 * @returns Formatted date string (e.g., "Jan 15, 2024")
 */
export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(date));
}

const utcDateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

export function formatDateUtc(date: Date | string): string {
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime())
    ? "N/A"
    : utcDateFormatter.format(parsed);
}

/**
 * Formats a date with time for display
 * @param date - Date to format
 * @returns Formatted date-time string (e.g., "Jan 15, 2024, 02:30 PM")
 */
export function formatDateTime(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(date));
}

/**
 * Formats an enrichment status for display
 * @param status - Enrichment status string
 * @returns Human-readable status text
 */
export function getEnrichmentStatusText(status: string): string {
  switch (status) {
    case "COMPLETED":
      return "Complete";
    case "PENDING":
      return "Pending";
    case "ERROR":
      return "Error";
    case "NOT_STARTED":
    default:
      return "Not Started";
  }
}

/**
 * Gets the CSS class for enrichment status styling
 * @param status - Enrichment status string
 * @returns CSS class string for styling
 */
export function getEnrichmentStatusColor(status: string): string {
  switch (status) {
    case "COMPLETED":
      return "text-green-600";
    case "PENDING":
      return "text-yellow-600";
    case "ERROR":
      return "text-red-600";
    case "NOT_STARTED":
    default:
      return "text-gray-500";
  }
}

/**
 * Check if an invitation has expired
 * @param expiresAt - The expiration date as a string or Date
 * @returns True if the invitation has expired, false otherwise
 */
export function isExpired(expiresAt: string | Date): boolean {
  return new Date(expiresAt) < new Date();
}

export type MatchScoreCategory = "strong" | "moderate" | "weak";

const MATCH_STRENGTH_BADGE_LAYOUT_CLASS =
  "min-w-[5.75rem] justify-center text-center";

export function getMatchStrengthBadgeClasses(
  category: MatchScoreCategory | null | undefined
): string {
  switch (category) {
    case "strong":
      return `${MATCH_STRENGTH_BADGE_LAYOUT_CLASS} border-[var(--match-strength-strong-border)] bg-[var(--match-strength-strong-bg)] text-[var(--match-strength-strong-text)]`;
    case "moderate":
      return `${MATCH_STRENGTH_BADGE_LAYOUT_CLASS} border-[var(--match-strength-moderate-border)] bg-[var(--match-strength-moderate-bg)] text-[var(--match-strength-moderate-text)]`;
    case "weak":
      return `${MATCH_STRENGTH_BADGE_LAYOUT_CLASS} border-[var(--match-strength-weak-border)] bg-[var(--match-strength-weak-bg)] text-[var(--match-strength-weak-text)]`;
    default:
      return `${MATCH_STRENGTH_BADGE_LAYOUT_CLASS} border-[#624B57] bg-[rgba(222,215,228,0.35)] text-[#624B57] dark:border-[var(--muted-foreground)] dark:bg-[rgba(255,255,255,0.06)] dark:text-[var(--muted-foreground)]`;
  }
}

export function getMatchStrengthOutlineClasses(
  category: MatchScoreCategory | null | undefined
): string {
  switch (category) {
    case "strong":
      return "border-[var(--match-strength-strong-border)] text-[var(--match-strength-strong-text)] dark:text-[var(--yield-celery-light)]";
    case "moderate":
      return "border-[var(--match-strength-moderate-border)] text-[var(--match-strength-moderate-text)] dark:text-[var(--yield-amber-100)]";
    case "weak":
      return "border-[var(--match-strength-weak-border)] text-[var(--match-strength-weak-text)] dark:text-[var(--enrichment-error-text)]";
    default:
      return "border-[#624B57] text-[#624B57] dark:border-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]";
  }
}

export function getMatchStrengthTextClasses(
  category: MatchScoreCategory | null | undefined
): string {
  switch (category) {
    case "strong":
      return "text-[var(--match-strength-strong-text)] dark:text-[var(--yield-celery-light)]";
    case "moderate":
      return "text-[var(--match-strength-moderate-text)] dark:text-[var(--yield-amber-100)]";
    case "weak":
      return "text-[var(--match-strength-weak-text)] dark:text-[var(--enrichment-error-text)]";
    default:
      return "text-gray-600 dark:text-[var(--muted-foreground)]";
  }
}

/**
 * Maps a normalized match score (0-100) to a display category.
 */
export function getMatchScoreCategory(score: number | null | undefined): {
  key: MatchScoreCategory;
  label: "Strong" | "Moderate" | "Weak";
} | null {
  if (score === null || score === undefined) {
    return null;
  }

  const value = typeof score === "number" ? score : Number(score);
  if (!Number.isFinite(value)) {
    return null;
  }

  if (value >= 90) {
    return { key: "strong", label: "Strong" };
  }
  if (value >= 70) {
    return { key: "moderate", label: "Moderate" };
  }
  return { key: "weak", label: "Weak" };
}

/**
 * Flags scores that should be reviewed closely.
 */
export function isMatchScoreFlaggedForReview(
  score: number | null | undefined
): boolean {
  if (score === null || score === undefined) {
    return false;
  }

  const value = typeof score === "number" ? score : Number(score);
  if (!Number.isFinite(value)) {
    return false;
  }
  return value < 40;
}

export const UNASSIGNED_PRODUCT_GROUP_NAME = "Unassigned";

export function isUnassignedProductGroupName(
  name: string | null | undefined
): boolean {
  return Boolean(name && name.trim() === UNASSIGNED_PRODUCT_GROUP_NAME);
}

export function getProductGroupDisplayName(
  name: string | null | undefined
): string {
  if (!name) return "";
  const trimmed = name.trim();
  return isUnassignedProductGroupName(trimmed) ? "" : trimmed;
}

export function extractPlatformFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace(/^www\./, ""); // Remove www.

    // Map common domains to branded names
    const platformMap: Record<string, string> = {
      "doordash.com": "DoorDash",
      "ubereats.com": "Uber Eats",
      "grubhub.com": "Grubhub",
      "postmates.com": "Postmates",
      "mcdonalds.com": "McDonald's",
      "burgerking.com": "Burger King",
      "tacobell.com": "Taco Bell",
      "wendys.com": "Wendy's",
      "kfc.com": "KFC",
      "subway.com": "Subway",
      "pizzahut.com": "Pizza Hut",
      "dominos.com": "Domino's",
      "seamless.com": "Seamless",
      "yelp.com": "Yelp",
      "zomato.com": "Zomato",
      "deliveroo.com": "Deliveroo",
      "just-eat.com": "Just Eat",
    };

    // Check if we have a branded name for this domain
    if (platformMap[hostname]) {
      return platformMap[hostname];
    }

    // For unknown domains, extract the main domain name (not subdomain)
    // e.g., "subdomain.foodrestau.com" → "Foodrestau"
    // e.g., "example.com" → "Example"
    const parts = hostname.split(".");

    // If there are 3+ parts (subdomain.domain.tld), get the second-to-last part (domain)
    // Otherwise, get the first part
    const domainName =
      (parts.length >= 3 ? parts[parts.length - 2] : parts[0]) || "website";

    // Capitalize the first letter
    return domainName.charAt(0).toUpperCase() + domainName.slice(1);
  } catch {
    return "Website";
  }
}

/**
 * Gets a unique color for a location based on its index
 * @param index - The index of the location in the array
 * @returns An object with color hex codes for the location marker and circle
 */
export function getLocationColor(index: number): {
  color: string;
  fillColor: string;
} {
  // Yield-branded palette with enough separation for multiple locations
  const colors = [
    { color: "#306572", fillColor: "#98B2B9" }, // Dashboard blue
    { color: "#819C6A", fillColor: "#C0D7AA" }, // Yield green
    { color: "#CB6F28", fillColor: "#F9CE6F" }, // Amber orange
    { color: "#C9BCCD", fillColor: "#DED7E4" }, // Plum
    { color: "#B7495D", fillColor: "#EDD6CF" }, // Rose
    { color: "#223136", fillColor: "#E2EFD5" }, // Aubergine + mint
    { color: "#546E7A", fillColor: "#D6E0E5" }, // Muted slate
    { color: "#A15B22", fillColor: "#FDE6B9" }, // Warm amber
    { color: "#6F875A", fillColor: "#E2EFD5" }, // Deep green
    { color: "#8C3240", fillColor: "#EDD6CF" }, // Deep rose
    { color: "#4F3D47", fillColor: "#DED7E4" }, // Dark plum
    { color: "#273E44", fillColor: "#98B2B9" }, // Deep blue
  ];

  return colors[index % colors.length]!;
}

/**
 * Formats a file size in bytes to a human-readable string
 * @param size - File size in bytes
 * @returns Formatted file size string (e.g., "1.5 MB")
 */
export function formatFileSize(size?: number): string {
  if (typeof size !== "number" || Number.isNaN(size)) {
    return "—";
  }
  if (size === 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1
  );
  const value = size / Math.pow(1024, exponent);
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

/**
 * Formats a timestamp string for display
 * @param timestamp - ISO timestamp string
 * @returns Formatted date-time string or "Unknown"
 */
export function formatLastModified(timestamp?: string): string {
  if (!timestamp) {
    return "Unknown";
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return date.toLocaleString();
}

/**
 * Creates a Content-Disposition header value for file downloads
 * @param fileName - Name of the file to download
 * @returns Content-Disposition header string
 */
export function createContentDispositionHeader(fileName: string): string {
  const fallbackName = fileName.replace(/[^\w.-]+/g, "_");
  const encodedName = encodeRFC5987ValueChars(fileName);
  return `attachment; filename="${fallbackName}"; filename*=UTF-8''${encodedName}`;
}

/**
 * Encodes a string according to RFC 5987 value encoding rules
 * @param value - String to encode
 * @returns RFC 5987 encoded string
 */
export function encodeRFC5987ValueChars(value: string): string {
  return encodeURIComponent(value).replace(
    /[!'()*]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`
  );
}

/**
 * Formats a label value for display (e.g., "HIGH" -> "High")
 * @param value - Label value to format
 * @returns Formatted string with first letter uppercase and rest lowercase
 */
export function formatLabel(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase();
}

/**
 * Formats a relevance label for display (e.g., "MENU_CHANGE" -> "Menu Change")
 * @param relevance - Relevance value to format
 * @returns Formatted string with words separated by spaces and each word capitalized
 */
export function formatRelevanceLabel(relevance: string): string {
  return relevance
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
}
